// Phase 1A Step 37 — Unified Shell icons + wordmark mark.
//
// Five Adinkra-derived geometric placeholders + the small STRIKZA mark
// used inside the 36px context strip. Adapted from
// handoff_phantom_mode/prototype/icons.jsx with the same visual DNA.
//
// IMPORTANT: These are PLACEHOLDERS. Per the prototype source comment:
//   "TODO: Replace with commissioned Adinkra icon set from human
//    illustrator. Each icon is a 24×24 simplified geometric
//    interpretation of its Adinkra symbol's visual DNA — not the real
//    symbol. Marked CLEARLY as placeholder."
//
// All icons are pure inline SVG, use `currentColor` (or the optional
// `color` prop), and contain no library imports, no emoji, no
// hardcoded hex except `var(--sz-amber)` referenced by the wordmark
// mark which is the brand glyph.

import type { CSSProperties } from "react";
import type { ModeKey } from "../modes/mode-config.ts";

const STROKE = 1.4;

interface IconProps {
  size?: number;
  color?: string;
}

// SANKOFA — bird looking back; "return / learn from past." The Pitch.
function IconSankofa({ size = 22, color = "currentColor" }: IconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-label="Sankofa (placeholder)"
    >
      <path
        d="M5 14 C 5 7, 12 4, 17 7 L 19 5 M 19 5 L 19 9 M 19 5 L 15 5"
        stroke={color}
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M 5 14 C 5 18, 9 20, 12 20"
        stroke={color}
        strokeWidth={STROKE}
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="9" cy="14" r="1.4" fill={color} />
    </svg>
  );
}

// GYE NYAME — supremacy; ringed dot. The Vault.
function IconGyeNyame({ size = 22, color = "currentColor" }: IconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-label="Gye Nyame (placeholder)"
    >
      <circle cx="12" cy="12" r="8" stroke={color} strokeWidth={STROKE} />
      <path
        d="M 12 4 L 12 20 M 4 12 L 20 12"
        stroke={color}
        strokeWidth={STROKE}
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="2.4" fill={color} />
    </svg>
  );
}

// DWENNIMMEN — ram's horns; humility & strength. The Brief.
function IconDwennimmen({ size = 22, color = "currentColor" }: IconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-label="Dwennimmen (placeholder)"
    >
      <path
        d="M 12 5 C 5 7, 5 17, 12 19 C 19 17, 19 7, 12 5 Z"
        stroke={color}
        strokeWidth={STROKE}
        fill="none"
      />
      <path
        d="M 8 10 C 8 13, 10 14, 12 14 C 14 14, 16 13, 16 10"
        stroke={color}
        strokeWidth={STROKE}
        fill="none"
        strokeLinecap="round"
      />
      <circle cx="12" cy="11.5" r="1" fill={color} />
    </svg>
  );
}

// MPATAPO — knot of pacification. The Council.
function IconMpatapo({ size = 22, color = "currentColor" }: IconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-label="Mpatapo (placeholder)"
    >
      <circle cx="9" cy="12" r="4.6" stroke={color} strokeWidth={STROKE} />
      <circle cx="15" cy="12" r="4.6" stroke={color} strokeWidth={STROKE} />
      <path
        d="M 9 7.4 L 9 16.6 M 15 7.4 L 15 16.6"
        stroke={color}
        strokeWidth={STROKE * 0.7}
        opacity="0.6"
      />
    </svg>
  );
}

// AYA — fern; endurance. The Exchange.
function IconAya({ size = 22, color = "currentColor" }: IconProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-label="Aya (placeholder)">
      <path d="M 12 4 L 12 20" stroke={color} strokeWidth={STROKE} strokeLinecap="round" />
      <path
        d="M 12 7 L 7 5 M 12 10 L 6 8 M 12 13 L 5 12 M 12 16 L 6 16 M 12 19 L 8 19"
        stroke={color}
        strokeWidth={STROKE}
        strokeLinecap="round"
        opacity="0.85"
      />
      <path
        d="M 12 7 L 17 5 M 12 10 L 18 8 M 12 13 L 19 12 M 12 16 L 18 16 M 12 19 L 16 19"
        stroke={color}
        strokeWidth={STROKE}
        strokeLinecap="round"
        opacity="0.85"
      />
    </svg>
  );
}

// Small STRIKZA mark — two stacked angled parallelograms in amber.
// Used inside the 36px context strip; existing `<Brand>` is sized for
// hero usage (28px) and is too tall for the strip.
interface StrikzaMarkProps {
  size?: number;
  style?: CSSProperties;
}

export function StrikzaMark({ size = 16, style }: StrikzaMarkProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-label="STRIKZA"
      style={style}
    >
      <path d="M 6 6 L 19 6 L 22 11 L 9 11 Z" fill="var(--sz-amber)" />
      <path d="M 2 13 L 15 13 L 18 18 L 5 18 Z" fill="var(--sz-amber)" opacity="0.6" />
    </svg>
  );
}

export interface ModeIconEntry {
  Icon: (props: IconProps) => JSX.Element;
  // Display label rendered by tooltips and aria-labels.
  label: string;
  // Adinkra name shown in the tooltip subtitle.
  symbol: string;
}

// Mode → icon map. Keyed by `ModeKey` from src/modes/mode-config.ts so
// that adding a sixth mode is a compile error here, not a silent
// fall-through.
export const UNIFIED_MODE_ICONS: { readonly [K in ModeKey]: ModeIconEntry } = {
  pitch: { Icon: IconSankofa, label: "The Pitch", symbol: "Sankofa" },
  vault: { Icon: IconGyeNyame, label: "The Vault", symbol: "Gye Nyame" },
  brief: { Icon: IconDwennimmen, label: "The Brief", symbol: "Dwennimmen" },
  council: { Icon: IconMpatapo, label: "The Council", symbol: "Mpatapo" },
  exchange: { Icon: IconAya, label: "The Exchange", symbol: "Aya" },
};
